"use client"

import { motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { FileText, Scale, ShoppingCart, Truck, CreditCard, AlertTriangle } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import FloatingCard from "@/components/animated/floating-card"
import GradientBackground from "@/components/animated/gradient-background"

export default function TermsClientPage() {
  const sections = [
    {
      icon: Scale,
      title: "General Terms",
      content: [
        "These terms govern your use of Real Metro Metal's services and website",
        "By using our services, you agree to be bound by these terms",
        "We reserve the right to modify these terms at any time",
        "Continued use after changes constitutes acceptance of new terms",
        "These terms are governed by Sri Lankan law",
      ],
    },
    {
      icon: ShoppingCart,
      title: "Orders & Quotations",
      content: [
        "All quotations are valid for 30 days unless otherwise specified",
        "Orders are subject to product availability and credit approval",
        "Minimum order quantities may apply to certain products",
        "We reserve the right to refuse or cancel orders at our discretion",
        "Order confirmations will be sent via email or phone",
      ],
    },
    {
      icon: CreditCard,
      title: "Pricing & Payment",
      content: [
        "All prices are in Sri Lankan Rupees (LKR) unless otherwise stated",
        "Prices are subject to change without prior notice",
        "Payment terms are typically 30 days from invoice date",
        "Late payment charges may apply to overdue accounts",
        "We accept bank transfers, checks, and approved credit arrangements",
      ],
    },
    {
      icon: Truck,
      title: "Delivery & Risk",
      content: [
        "Delivery dates are estimates and not guaranteed",
        "Risk of loss passes to buyer upon delivery or pickup",
        "Delivery charges apply unless otherwise agreed",
        "Buyer is responsible for unloading and inspection upon delivery",
        "Claims for shortages or damage must be made within 48 hours",
      ],
    },
  ]

  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">
                <FileText className="h-4 w-4 mr-2" />
                Terms of Service
              </Badge>
            </motion.div>
            <TextReveal>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6">Terms & Conditions</h1>
            </TextReveal>
            <TextReveal delay={0.2}>
              <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
                Please read these terms and conditions carefully before using Real Metro Metal's services. These terms
                establish the legal framework for our business relationship.
              </p>
            </TextReveal>
            <TextReveal delay={0.4}>
              <p className="text-sm text-slate-400 mt-4">
                Last updated:{" "}
                {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-20 relative z-10">
        {/* Main Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {sections.map((section, index) => (
            <FloatingCard key={section.title} delay={index * 0.1}>
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-3 rounded-lg">
                      <section.icon className="h-6 w-6" />
                    </div>
                    <span className="text-xl">{section.title}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-slate-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </FloatingCard>
          ))}
        </div>

        {/* Additional Terms */}
        <div className="space-y-8">
          <FloatingCard delay={0.4}>
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <AlertTriangle className="h-6 w-6 text-slate-700" />
                  <span>Warranties & Limitations</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Product Warranties:</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Products are sold with manufacturer's standard warranties</li>
                    <li>• We do not provide additional warranties beyond manufacturer terms</li>
                    <li>• Warranty claims must be processed through manufacturers</li>
                    <li>• Installation and misuse voids all warranties</li>
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Limitation of Liability:</h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Our liability is limited to the purchase price of products</li>
                    <li>• We are not liable for consequential or indirect damages</li>
                    <li>• Products are sold for commercial/industrial use only</li>
                    <li>• Buyer assumes responsibility for product suitability</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </FloatingCard>

          <FloatingCard delay={0.5}>
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed mb-4">
                  For questions about these terms or our services, please contact us:
                </p>
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <p className="text-sm">
                    <strong>Business Name:</strong> Real Metro Metal
                  </p>
                  <p className="text-sm">
                    <strong>Phone:</strong> +94 77 393 3643
                  </p>
                  <p className="text-sm">
                    <strong>Address:</strong> 392 1/3 Old Moor St, Colombo 01200, Sri Lanka
                  </p>
                  <p className="text-sm">
                    <strong>Business Hours:</strong> Monday - Saturday, 8:00 AM - 6:00 PM
                  </p>
                </div>
              </CardContent>
            </Card>
          </FloatingCard>
        </div>
      </div>
    </div>
  )
}
